import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, BarChart3, Coins, Eye, RefreshCw, Scale, SearchX, Sparkles, Users, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AdminPage, EmptyState, KpiCard, PillTabs, RelativeTime, SearchField, SectionCard, TablePagination, formatNumber } from '../../../components/design-system/admin';
import { useFeatureUsageApi } from '../../../common/hooks/use-feature-usage-api.hook';
import type { FeatureKey, UsageDays, UsageOverview, VendorScope, VendorUsageRow } from '../../../common/models/feature-usage.model';
import { FEATURE_META, formatMetric, formatSpend, formatUsd, headline, metaOf } from '../../../modules/superadmin/vendor-usage/usage-meta';
import { FeatureStrip } from '../../../modules/superadmin/vendor-usage/FeatureStrip';

type SortKey = 'name' | 'lastSeen' | 'features' | 'aiCost' | 'credits' | 'feature';
type Filter = 'using' | 'not';

const DAYS: UsageDays[] = [7, 30, 90, 365];
const SCOPES: VendorScope[] = ['customers', 'prospects', 'all'];
const isFeature = (v: string | null): v is FeatureKey => !!v && FEATURE_META.some((f) => f.key === v);
/** Features whose numbers already have their own columns. */
const OWN_COLUMN: FeatureKey[] = ['ai', 'credits'];

/**
 * Super-admin Vendor usage: how vendors use the backoffice. Which features
 * each vendor uses and how much — AI (in $ and in credits), credits charged,
 * marketing emails, ads, content studio, the visualizer… — and how many
 * vendors use each feature. Choose a feature to see who uses it, and who does
 * not; open a vendor for its detail. Period, scope and filters live in the URL.
 */
export function VendorUsagePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useFeatureUsageApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [params, setParams] = useSearchParams();

  const days = (DAYS as number[]).includes(Number(params.get('days'))) ? (Number(params.get('days')) as UsageDays) : 30;
  const scope = (SCOPES as string[]).includes(params.get('scope') || '') ? (params.get('scope') as VendorScope) : 'customers';
  const feature = isFeature(params.get('feature')) ? (params.get('feature') as FeatureKey) : null;
  const filter: Filter = params.get('filter') === 'not' ? 'not' : 'using';

  const setParam = (key: string, value: string | null, defaultValue?: string) => {
    const p = new URLSearchParams(params);
    if (value === null || value === defaultValue) p.delete(key);
    else p.set(key, value);
    if (key === 'feature' && value === null) p.delete('filter');
    setParams(p, { replace: true });
    setPage(0);
  };

  const [data, setData] = useState<UsageOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'aiCost', desc: true });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(
    async (fresh = false) => {
      setLoading(true);
      setFailed(false);
      try {
        setData(await apiRef.current.overview(days, scope, fresh));
      } catch {
        setFailed(true);
        toast.error(t('ADMIN_USAGE.TOAST.LOAD_FAILED'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, scope],
  );

  useEffect(() => {
    load();
  }, [load]);

  // A feature chosen sorts by it.
  useEffect(() => {
    if (feature) setSort({ key: feature === 'ai' ? 'aiCost' : feature === 'credits' ? 'credits' : 'feature', desc: true });
  }, [feature]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    let list = (data?.vendors || []).filter((v) => !needle || [v.name, v.alias, v.plan].some((x) => String(x || '').toLowerCase().includes(needle)));
    if (feature) list = list.filter((v) => (filter === 'using' ? v.used.includes(feature) : !v.used.includes(feature)));
    const value = (v: VendorUsageRow): number | string => {
      switch (sort.key) {
        case 'name':
          return v.name.toLowerCase();
        case 'lastSeen':
          return v.lastSeenAt ? new Date(v.lastSeenAt).getTime() : 0;
        case 'features':
          return v.used.length;
        case 'credits':
          return v.metrics.creditsUsed || 0;
        case 'feature':
          return feature ? headline(feature, v.metrics) : 0;
        default:
          return v.metrics.aiCostUsd || 0;
      }
    };
    return [...list].sort((a, b) => {
      const x = value(a);
      const y = value(b);
      const c = x < y ? -1 : x > y ? 1 : a.name.localeCompare(b.name);
      return sort.desc ? -c : c;
    });
  }, [data, search, feature, filter, sort]);

  const shown = rows.slice(page * pageSize, (page + 1) * pageSize);
  const total = data?.vendorCount || 0;
  const totals = data?.totals || {};

  const SortHead = ({ k, children, className }: { k: SortKey; children: React.ReactNode; className?: string }) => (
    <TableHead className={className}>
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={() => setSort((s) => ({ key: k, desc: s.key === k ? !s.desc : k !== 'name' }))}
        data-testid={`usage-sort-${k}`}
      >
        {children}
        {sort.key === k && (sort.desc ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" />)}
      </button>
    </TableHead>
  );

  const featureName = (k: FeatureKey) => t(`ADMIN_USAGE.FEATURES.${k}`);

  return (
    <AdminPage
      title={t('ADMIN_USAGE.NAV')}
      parents={[{ label: t('ADMIN_SHELL.NAV.SECTION_CUSTOMERS') }]}
      description={t('ADMIN_USAGE.DESCRIPTION')}
      actions={
        <>
          {/* What each customer earns and costs us, and which features nobody pays to use. */}
          <Button variant="outline" size="sm" asChild>
            <Link to="/unit-economics" data-testid="usage-unit-economics-link">
              <Scale />
              {t('UNIT_ECONOMICS.OPEN')}
            </Link>
          </Button>
          <Select value={scope} onValueChange={(v) => setParam('scope', v, 'customers')}>
            <SelectTrigger className="h-9 w-[150px]" aria-label={t('ADMIN_USAGE.SCOPE.LABEL')} data-testid="usage-scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCOPES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`ADMIN_USAGE.SCOPE.${s.toUpperCase()}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <PillTabs
            value={days}
            onChange={(v) => setParam('days', String(v), '30')}
            ariaLabel={t('ADMIN_USAGE.PERIOD.LABEL')}
            options={DAYS.map((d) => ({ value: d, label: t(`ADMIN_USAGE.PERIOD.D${d}`) }))}
          />
          <Button variant="ghost" size="icon" onClick={() => load(true)} aria-label={t('ADMIN_USAGE.REFRESH')} disabled={loading}>
            <RefreshCw className={cn(loading && 'animate-spin')} />
          </Button>
        </>
      }
    >
      {failed && !data ? (
        <Card>
          <EmptyState
            icon={<BarChart3 />}
            title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
            action={
              <Button variant="outline" size="sm" onClick={() => load()}>
                {t('ADMIN_SHELL.COMMON.RETRY')}
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard
              label={t('ADMIN_USAGE.KPI.ACTIVE')}
              value={data ? `${data.activeVendors}/${total}` : '—'}
              hint={t('ADMIN_USAGE.KPI.ACTIVE_HINT')}
              icon={<Users />}
              loading={!data}
              testId="usage-kpi-active"
            />
            <KpiCard
              label={t('ADMIN_USAGE.KPI.AI_COST')}
              value={formatUsd(totals.aiCostUsd)}
              hint={t('ADMIN_USAGE.KPI.AI_COST_HINT', { credits: formatNumber(totals.aiCredits || 0), tokens: formatMetric('aiTokens', totals.aiTokens) })}
              icon={<Sparkles />}
              loading={!data}
              testId="usage-kpi-ai"
            />
            <KpiCard
              label={t('ADMIN_USAGE.KPI.CREDITS')}
              value={formatNumber(totals.creditsUsed || 0)}
              hint={t('ADMIN_USAGE.KPI.CREDITS_HINT', { usd: formatUsd((totals.creditsUsed || 0) * (data?.usdPerCredit || 0.01)) })}
              icon={<Coins />}
              loading={!data}
              testId="usage-kpi-credits"
            />
            <KpiCard
              label={t('ADMIN_USAGE.KPI.VISUALIZER')}
              value={formatNumber(totals.visualizerViews || 0)}
              hint={t('ADMIN_USAGE.KPI.VISUALIZER_HINT', { count: totals.visualizerVisitors || 0 })}
              icon={<Eye />}
              loading={!data}
            />
          </div>

          <SectionCard title={t('ADMIN_USAGE.ADOPTION.TITLE')} description={t('ADMIN_USAGE.ADOPTION.HINT')} testId="usage-adoption">
            {!data ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
                {FEATURE_META.map((f) => {
                  const n = data.adoption.find((a) => a.feature === f.key)?.vendors || 0;
                  const pct = total ? Math.round((100 * n) / total) : 0;
                  const active = feature === f.key;
                  const Icon = f.icon;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setParam('feature', active ? null : f.key)}
                      aria-pressed={active}
                      className={cn(
                        'grid gap-2 rounded-xl border p-3 text-start transition-colors hover:bg-accent/50',
                        active && 'outline outline-[1.5px] outline-foreground',
                      )}
                      data-testid={`usage-adoption-${f.key}`}
                    >
                      <span className="flex items-center gap-2 text-sm">
                        <span className="flex size-6 items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <Icon className="size-3.5" />
                        </span>
                        <span className="truncate">{featureName(f.key)}</span>
                      </span>
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="font-heading text-lg tabular-nums">{n}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">{t('ADMIN_USAGE.ADOPTION.OF', { total, pct })}</span>
                      </span>
                      <span className="h-1 overflow-hidden rounded-full bg-muted">
                        <span className="block h-full rounded-full bg-foreground/70" style={{ width: `${pct}%` }} />
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {feature ? (
                <>
                  <span className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm" data-testid="usage-feature-filter">
                    {featureName(feature)}
                    <button type="button" onClick={() => setParam('feature', null)} aria-label={t('ADMIN_USAGE.TABLE.CLEAR_FEATURE')}>
                      <X className="size-3.5 text-muted-foreground hover:text-foreground" />
                    </button>
                  </span>
                  <PillTabs
                    value={filter}
                    onChange={(v) => setParam('filter', v, 'using')}
                    ariaLabel={t('ADMIN_USAGE.TABLE.FILTER')}
                    options={[
                      { value: 'using' as Filter, label: t('ADMIN_USAGE.TABLE.USING') },
                      { value: 'not' as Filter, label: t('ADMIN_USAGE.TABLE.NOT_USING') },
                    ]}
                  />
                </>
              ) : (
                <span className="text-sm text-muted-foreground">{t('ADMIN_USAGE.TABLE.PICK_FEATURE')}</span>
              )}
            </div>
            <SearchField value={search} onChange={(v) => { setSearch(v); setPage(0); }} placeholder={t('ADMIN_USAGE.TABLE.SEARCH')} />
          </div>

          <Card className="gap-0 overflow-hidden p-0">
            {!data ? (
              <div className="grid gap-2 p-4" data-testid="usage-loading">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-11 w-full" />
                ))}
              </div>
            ) : !rows.length ? (
              <EmptyState icon={<SearchX />} title={t('ADMIN_USAGE.TABLE.EMPTY_TITLE')} description={t('ADMIN_USAGE.TABLE.EMPTY_BODY')} />
            ) : (
              <>
                <Table data-testid="usage-table" className={cn(loading && 'opacity-60 transition-opacity')}>
                  <TableHeader>
                    <TableRow>
                      <SortHead k="name" className="ps-4">{t('ADMIN_USAGE.TABLE.VENDOR')}</SortHead>
                      <SortHead k="lastSeen">{t('ADMIN_USAGE.TABLE.LAST_SEEN')}</SortHead>
                      <SortHead k="features">{t('ADMIN_USAGE.TABLE.FEATURES')}</SortHead>
                      {feature && !OWN_COLUMN.includes(feature) && <SortHead k="feature">{featureName(feature)}</SortHead>}
                      <SortHead k="aiCost" className="text-end">{t('ADMIN_USAGE.TABLE.AI')}</SortHead>
                      <SortHead k="credits" className="pe-4 text-end">{t('ADMIN_USAGE.TABLE.CREDITS')}</SortHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shown.map((v) => (
                      <TableRow key={v._id} className="cursor-pointer" onClick={() => navigate(`/vendors/${v._id}/edit?tab=usage`)} data-testid={`usage-row-${v._id}`}>
                        <TableCell className="max-w-[240px] ps-4">
                          <div className="truncate font-medium">{v.name}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {[v.plan, v.kind !== 'customer' ? t(`ADMIN_USAGE.KIND.${v.kind.toUpperCase()}`) : null, !v.isEnabled ? t('ADMIN_USAGE.DISABLED') : null].filter(Boolean).join(' · ')}
                          </div>
                        </TableCell>
                        <TableCell>{v.lastSeenAt ? <RelativeTime value={v.lastSeenAt} className="text-muted-foreground" /> : <span className="text-muted-foreground">{t('ADMIN_USAGE.NEVER')}</span>}</TableCell>
                        <TableCell>
                          <FeatureStrip used={v.used} highlight={feature} />
                        </TableCell>
                        {feature && !OWN_COLUMN.includes(feature) && (
                          <TableCell className="tabular-nums" data-testid={`usage-feature-cell-${v._id}`}>
                            <div>{formatMetric(metaOf(feature).metrics[0], headline(feature, v.metrics))}</div>
                            <div className="text-xs text-muted-foreground">
                              {feature === 'ads' && formatSpend(v.spend)
                                ? formatSpend(v.spend)
                                : t(`ADMIN_USAGE.METRICS.${metaOf(feature).metrics[0]}`)}
                            </div>
                          </TableCell>
                        )}
                        <TableCell className="text-end tabular-nums">
                          <div>{formatUsd(v.metrics.aiCostUsd)}</div>
                          <div className="text-xs text-muted-foreground">
                            {t('ADMIN_USAGE.TABLE.AI_SUB', { credits: formatNumber(v.metrics.aiCredits || 0), tokens: formatMetric('aiTokens', v.metrics.aiTokens) })}
                          </div>
                        </TableCell>
                        <TableCell className="pe-4 text-end tabular-nums">
                          <div>{formatNumber(v.metrics.creditsUsed || 0)}</div>
                          <div className="text-xs text-muted-foreground">
                            {v.creditBalance === null ? (
                              '—'
                            ) : v.creditBalance < 0 ? (
                              <span className="text-destructive">{t('ADMIN_USAGE.TABLE.OVERDRAWN', { count: -v.creditBalance })}</span>
                            ) : (
                              t('ADMIN_USAGE.TABLE.BALANCE', { count: v.creditBalance })
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  page={page}
                  pageSize={pageSize}
                  total={rows.length}
                  onPageChange={setPage}
                  onPageSizeChange={(s) => {
                    setPageSize(s);
                    setPage(0);
                  }}
                />
              </>
            )}
          </Card>
        </>
      )}
    </AdminPage>
  );
}

export default VendorUsagePage;
